// externals
import * as React from 'react';

// libraries
import { INextStepCardDetails } from '@makemydeal/dr-activities-common';
import { IconFactory } from '@makemydeal/dr-activities-common';

// utils
import { formatDollarsAndCents } from '../../../../store/mmd/formatUtils';
import { milesToStringShort } from '../../../../store/mmd/uiHelpersUtil';

// config
import config from '../../../../store/mmd/config';

// components
import TradePage from '../../../../common/components/TradePage/TradePage';
import { errorMessageUserIsDealer, getErrorMessageUserIsNotDealer } from '../ManualSummary/constants';
import useGTPRulesModal from '../../../../common/components/subaruEligibility/useGTPRulesModal';
import GTPRulesTrigger from '../../../../common/components/subaruEligibility/GTPRulesTrigger';
import GTPRulesModal from '../../../../common/components/subaruEligibility/GTPRulesModal';

// types
import { StepperRoute } from '../../../../types';

export interface IGTPSummaryUIStateProps {
    calculatedTradeInValue: number;
    cardDetails: INextStepCardDetails;
    dealerPhoneNumber: string;
    isLeadFormEnabled: boolean;
    isLeadFormSubmitted: boolean;
    isTradeLeadFormEnabled: boolean;
    loanBalance: number;
    make: string;
    model: string;
    stepperRoutes?: StepperRoute[];
    subaruGtpLogoAlt?: any; // This is purely for local dev use (storybook can't show the static assets)
    tradeInValue: number;
    tradePaymentFailed: boolean;
    userIsDealer: boolean;
    userIsDesking: boolean;
    vehicleCondition: string;
    vehicleMileage: number;
    year: string;
    ghostVinDealerConfig: boolean;
    isGhostVinEnabled: boolean;
    actionButtonText: string;
    showActionButton: boolean;
    backRoute: string;
    isMultiTradeEnabled?: boolean;
    shouldIncludeTradeIn?: boolean;
    urlPath?: string;
    hasAcv?: boolean;
    expirationDate?: string | null;
}

export interface IManualSummaryUIDispatchProps {
    dispatchAnalytics: (type: string, description: string) => void;
    editTradeIn: () => void;
    goToRoute: (route: string) => void;
    loadLeadForm: (skiplink: boolean) => void;
    removeTrade: () => void;
    termsDetailsClicked: () => void;
    tradeInEstimateDisplayed: () => void;
    goToNextStep: () => void;
    cdlBackButtonClicked: () => void;
    addRemoveTradeToDeal: (shouldIncludeTradeIn: boolean) => void;
}

export interface IManualSummaryUIProps extends IGTPSummaryUIStateProps, IManualSummaryUIDispatchProps {}

const SubaruGTPSummaryUI: React.FC<IManualSummaryUIProps> = (props) => {
    const {
        calculatedTradeInValue,
        dealerPhoneNumber,
        dispatchAnalytics,
        goToRoute,
        hasAcv,
        isLeadFormEnabled,
        isLeadFormSubmitted,
        isTradeLeadFormEnabled,
        loanBalance,
        make,
        model,
        stepperRoutes,
        subaruGtpLogoAlt,
        tradeInValue,
        tradePaymentFailed,
        userIsDealer,
        userIsDesking,
        vehicleCondition,
        vehicleMileage,
        year,
        ghostVinDealerConfig,
        isGhostVinEnabled,
        actionButtonText,
        showActionButton,
        goToNextStep,
        removeTrade,
        backRoute,
        cdlBackButtonClicked,
        isMultiTradeEnabled,
        addRemoveTradeToDeal,
        shouldIncludeTradeIn,
        loadLeadForm,
        tradeInEstimateDisplayed,
        editTradeIn,
        urlPath,
        expirationDate
    } = props;

    const [showPaymentError, setShowPaymentError] = React.useState(tradePaymentFailed);
    const { showModal, openModal, closeModal } = useGTPRulesModal();

    React.useEffect(() => {
        if (!isLeadFormSubmitted && isLeadFormEnabled) {
            if (!userIsDealer) {
                loadLeadForm(!isTradeLeadFormEnabled);
            } else {
                tradeInEstimateDisplayed();
            }
        } else {
            tradeInEstimateDisplayed();
        }
    }, []);

    React.useEffect(() => {
        setShowPaymentError(tradePaymentFailed);
    }, [tradePaymentFailed]);

    const prevIsLeadFormSubmitted = React.useRef(isLeadFormSubmitted);
    React.useEffect(() => {
        if (prevIsLeadFormSubmitted.current === false && isLeadFormSubmitted) {
            tradeInEstimateDisplayed();
        }
        prevIsLeadFormSubmitted.current = isLeadFormSubmitted;
    }, [isLeadFormSubmitted]);

    const { staticImages } = config;

    const pencilIcon = <IconFactory icon="pencilIcon" className="icon primary-link-icon" staticImages={staticImages} />;
    const editText = <span className="primary-link">Edit</span>;
    const shouldDisplayConditionLabel = vehicleCondition !== '';
    const subaruGtpLogo = subaruGtpLogoAlt || (
        <IconFactory icon="subaruGtpLogo" className="subaru-gtp-logo" staticImages={staticImages} />
    );

    let dealerDealDescriptionText = '';
    let dealerDealLinkText = '';

    if (isGhostVinEnabled) {
        dealerDealDescriptionText = ghostVinDealerConfig ? 'Submit the completed deal to the CRM for review' : '';
        dealerDealLinkText = ghostVinDealerConfig ? 'Send to CRM' : 'Submit for Review';
    }

    const handleSkipButtonClick = () => {
        removeTrade();
    };

    const handleBackButtonClick = () => {
        cdlBackButtonClicked();
        goToRoute(backRoute);
    };

    const errorMessage = userIsDealer ? errorMessageUserIsDealer : getErrorMessageUserIsNotDealer(dealerPhoneNumber);

    const renderError = () =>
        showPaymentError ? (
            <div className="trade-in-payment-failed">
                <i className="fa fa-info-circle"></i>
                <p>
                    {errorMessage}
                    <a href="#" onClick={() => removeTrade()}>
                        Remove Trade-In
                    </a>
                </p>
            </div>
        ) : null;

    const renderTradeConfirmationHeader = () => (
        <div className="trade-in-iw-estimate">
            {tradeInValue ? (
                <IconFactory icon="checkmark" className="icon checkmark" staticImages={staticImages} />
            ) : (
                <IconFactory icon="clock" className="icon clock" staticImages={staticImages} />
            )}
            <div className="estimated-value">
                <div className="estimated-value__teaser common-header">
                    Your {year} {make} {model} has an
                </div>
                <div className={`estimated-value__content${tradeInValue === 0 || !tradeInValue ? '__pa' : ''} common-header`}>
                    <div className="estimated-value__content--label">Estimated Trade-In Value*:&nbsp;</div>
                    {tradeInValue && calculatedTradeInValue > 0 && (
                        <div className="estimated-value__content--value positive">
                            {formatDollarsAndCents(calculatedTradeInValue)}
                        </div>
                    )}
                    {tradeInValue && calculatedTradeInValue < 0 && (
                        <div className="estimated-value__content--value negative">
                            {formatDollarsAndCents(calculatedTradeInValue)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <TradePage
                urlPath={urlPath}
                showCustomTitle
                headerOnTop
                stepperRoutes={stepperRoutes}
                isStepperClickable={false}
                stepLabelVisible={true}
                pageTitle="We've Added Your Trade-In."
                pageSubTitle=""
                pageClass="trade-in-summary-page"
                showShareDeal={false}
                isDealerUser={userIsDealer}
                isDeskingUser={userIsDesking}
                staticAssetsConfig={staticImages}
                cardDetails={null}
                goToRoute={goToRoute}
                dispatchAnalytics={dispatchAnalytics}
                renderError={renderError}
                dealerDealDescription={dealerDealDescriptionText}
                dealerDealLinkText={dealerDealLinkText}
                {...(!isMultiTradeEnabled && {
                    footerProps: {
                        onActionButtonClick: showActionButton ? goToNextStep : undefined,
                        buttonText: actionButtonText,
                        showBackButton: true,
                        showSkipButton: true,
                        skipButtonText: 'Remove Trade-In',
                        fullWidthLayout: true,
                        onSkipButtonClick: handleSkipButtonClick,
                        onBackButtonClick: handleBackButtonClick
                    }
                })}
            >
                <div className="trade-in-summary-content">
                    {renderTradeConfirmationHeader()}
                    <div className="trade-in-iw-vehicle container-fluid">
                        <div className="vehicle row">
                            <div className="vehicle column">
                                <div className="row">
                                    <div className="vehicle-info-details">
                                        <div className="estimate-header">How'd we come up with the value?</div>
                                        <div className="vehicle-info__content">
                                            <div className="math-stack-iw-container">
                                                <div className="math-stack-iw">
                                                    <div className="math-stack-iw-label subaru-gtp-label">
                                                        <div>Subaru Guaranteed </div>
                                                        <div>Trade-In Value</div>
                                                    </div>
                                                    <div className="math-stack-iw-value">
                                                        {!!tradeInValue && formatDollarsAndCents(tradeInValue)}
                                                    </div>
                                                </div>{' '}
                                                {loanBalance > 0 && (
                                                    <div className="math-stack-iw">
                                                        <div className="math-stack-iw-label">Amount You Owe</div>
                                                        <div className="math-stack-iw-value">
                                                            {loanBalance ? '-' : ''}
                                                            {formatDollarsAndCents(loanBalance)}
                                                        </div>
                                                    </div>
                                                )}
                                                <hr className="bar" />
                                                <div className="math-stack-iw">
                                                    <div className="math-stack-iw-label">Trade-In Value</div>
                                                    {tradeInValue && calculatedTradeInValue > 0 && (
                                                        <div className="math-stack-iw-value positive">
                                                            {formatDollarsAndCents(calculatedTradeInValue)}
                                                        </div>
                                                    )}
                                                    {tradeInValue && calculatedTradeInValue < 0 && (
                                                        <div className="math-stack-iw-value negative">
                                                            {formatDollarsAndCents(calculatedTradeInValue)}
                                                        </div>
                                                    )}
                                                    {!tradeInValue && (
                                                        <div className="math-stack-iw-value">
                                                            Pending <br className="hide-lg" />
                                                            Appraisal
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="vehicle-info common-header">
                                        <div className="estimate-header flex">
                                            <div className="bold">Trade-In Details</div>
                                            {!hasAcv && (
                                                <div className={'primary-link-container pencil-edit-icon'} onClick={editTradeIn}>
                                                    {pencilIcon}
                                                    {editText}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="vehicle-info__content">
                                        <div className="vehicle-info__contentLeft">
                                            <div className="math-stack-iw-container">
                                                <div className="math-stack-iw">
                                                    <div className="math-stack-iw-label">Year</div>
                                                    <div className="math-stack-iw-value">{year}</div>
                                                </div>
                                                <div className="math-stack-iw">
                                                    <div className="math-stack-iw-label">Make</div>
                                                    <div className="math-stack-iw-value">{make}</div>
                                                </div>
                                                <div className="math-stack-iw">
                                                    <div className="math-stack-iw-label">Model</div>
                                                    <div className="math-stack-iw-value">{model}</div>
                                                </div>
                                                {shouldDisplayConditionLabel && (
                                                    <div className="math-stack-iw">
                                                        <div className="math-stack-iw-label">Condition</div>
                                                        <div className="math-stack-iw-value">{vehicleCondition}</div>
                                                    </div>
                                                )}
                                                <div className="math-stack-iw">
                                                    <div className="math-stack-iw-label">Mileage</div>
                                                    <div className="math-stack-iw-value">{milesToStringShort(vehicleMileage)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="vehicle">
                                <div className="flex subaru-logo">{subaruGtpLogo}</div>
                            </div>
                        </div>
                        <div className="col-xs-12 trade-in-vehicle__gtprules">
                            Subaru Guaranteed Trade Program value is subject to verification.
                            {expirationDate && (
                                <span> Estimate valid through {new Date(expirationDate).toLocaleDateString()}.</span>
                            )}
                            &nbsp;See&nbsp;
                            <GTPRulesTrigger onOpen={openModal} />
                        </div>
                    </div>
                </div>
            </TradePage>
            {isMultiTradeEnabled && (
                <div className="trade-summary-footer">
                    <button className="back-button" onClick={handleBackButtonClick}>
                        <span className="back-icon">&lt;</span> Back
                    </button>
                    <div className="right-actions">
                        <div className="apply-trade-toggle">
                            <label className="toggle-label">
                                Apply Trade to Deal
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={shouldIncludeTradeIn}
                                        onChange={() => addRemoveTradeToDeal(!!shouldIncludeTradeIn)}
                                    />
                                    <span className="slider"></span>
                                </div>
                            </label>
                        </div>
                        <button className="delete-trade-button" onClick={handleSkipButtonClick}>
                            Delete Trade-In
                        </button>
                    </div>
                </div>
            )}
            <GTPRulesModal show={showModal} onClose={closeModal} dispatchAnalytics={dispatchAnalytics} />
        </>
    );
};

export default SubaruGTPSummaryUI;
